/**
 * CampaignQuickSendDialog
 * -----------------------
 * In-CRM modal that lets an admin/sales-rep:
 *   1. Pick an email campaign template (loaded from /api/email-templates)
 *   2. Optionally override the subject line
 *   3. Select recipients = PRIMARY contact only · ALL contacts on this account
 *      · custom subset (checkboxes)
 *   4. One-click send via /api/admin/email-campaign/send-custom
 *
 * The backend's send-custom endpoint already auto-logs one CRM activity
 * per matched hospital, so no extra activity insert is needed here.
 *
 * Special mode: when `account.__pickAccount === true`, the dialog first
 * shows a hospital picker pulled from the accounts list before continuing.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Send, Mail, ChevronLeft, ExternalLink, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CampaignQuickSendDialog({ headers, account, onClose, onSent }) {
  // If the caller passed __preselected contacts (e.g. clicked "Email" on a single
  // contact row or "Email N selected" from the bulk-action bar), open in Custom
  // mode with those contacts pre-checked and lock the picker for clarity.
  const preselectedIds = Array.isArray(account?.__preselected)
    ? account.__preselected.map((c) => c.id)
    : null;

  // Stage 1: optional account picker (only when triggered from the global Activities tab)
  const [picked, setPicked] = useState(account?.__pickAccount ? null : account);
  const [accountSearch, setAccountSearch] = useState("");

  // Stage 2: real workflow against `picked`
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [subject, setSubject] = useState("");
  const [recipientMode, setRecipientMode] = useState(preselectedIds ? "custom" : "primary"); // primary | all | custom
  const [customSelected, setCustomSelected] = useState({}); // contactId -> bool
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState(null);

  // Eagerly load templates only after an account is chosen
  useEffect(() => {
    if (!picked) return;
    setLoadingTemplates(true);
    axios
      .get(`${API}/api/email-templates`, { headers })
      .then((r) => {
        const list = r.data?.templates || [];
        setTemplates(list);
        if (list.length > 0) {
          setTemplateId(list[0].id);
          setSubject(list[0].subject || "");
        }
      })
      .catch(() => toast.error("Failed to load templates"))
      .finally(() => setLoadingTemplates(false));
  }, [picked, headers]);

  // Update subject when the user switches templates
  useEffect(() => {
    if (!templateId || templates.length === 0) return;
    const t = templates.find((x) => x.id === templateId);
    if (t?.subject) setSubject(t.subject);
  }, [templateId, templates]);

  // Initialize custom-selection map from the picked account's contacts
  useEffect(() => {
    if (!picked?.contacts) return;
    const init = {};
    if (preselectedIds && preselectedIds.length > 0) {
      // Honor caller's pre-selection (single contact or bulk pick from drawer)
      picked.contacts.forEach((c) => { init[c.id] = preselectedIds.includes(c.id); });
    } else {
      picked.contacts.forEach((c) => { init[c.id] = !!c.is_primary; });
    }
    setCustomSelected(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked]);

  const accountContacts = picked?.contacts || [];
  const primary = accountContacts.find((c) => c.is_primary) || accountContacts[0];
  const validRecipients = useMemo(() => {
    if (!picked) return [];
    let arr;
    if (recipientMode === "primary") arr = primary ? [primary] : [];
    else if (recipientMode === "all") arr = accountContacts;
    else arr = accountContacts.filter((c) => customSelected[c.id]);
    return arr.filter((c) => c.email);
  }, [picked, recipientMode, accountContacts, primary, customSelected]);

  const filteredAccounts = useMemo(() => {
    if (!account?.accounts) return [];
    const q = accountSearch.trim().toLowerCase();
    return account.accounts
      .filter((a) => !q || (a.name || "").toLowerCase().includes(q))
      .slice(0, 60);
  }, [account, accountSearch]);

  const send = async () => {
    if (!templateId) { toast.error("Pick a template"); return; }
    if (validRecipients.length === 0) {
      toast.error("This recipient mode has no contacts with email addresses");
      return;
    }
    setSending(true);
    try {
      const payload = {
        template_id: templateId,
        subject,
        recipients: validRecipients.map((c) => ({
          name: c.name || c.email,
          email: c.email,
          company: picked.name,  // resolves the activity to this CRM account
        })),
      };
      const r = await axios.post(
        `${API}/api/admin/email-campaign/send-custom`,
        payload,
        { headers },
      );
      const d = r.data || {};
      toast.success(
        `Sent to ${d.success_count ?? validRecipients.length}/${d.total_recipients ?? validRecipients.length} · ${d.crm_activities_logged ?? 1} activity logged`
      );
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const loadPreview = async () => {
    if (!templateId) return;
    try {
      const r = await axios.get(`${API}/api/email-templates/${templateId}`, { headers });
      setPreview(r.data?.html || "");
      setShowPreview(true);
    } catch (e) {
      toast.error("Preview unavailable");
    }
  };

  // ============== STAGE 1: account picker ==============
  if (!picked) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="campaign-quick-send">
        <div className="bg-white rounded-lg w-full max-w-xl my-10 shadow-2xl">
          <div className="border-b px-6 py-4 flex justify-between items-center">
            <h3 className="font-bold text-lg flex items-center gap-2"><Send className="w-5 h-5 text-blue-600" /> Send Email Campaign</h3>
            <button onClick={onClose}><X className="w-5 h-5" /></button>
          </div>
          <div className="px-6 py-5 space-y-3">
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Pick an account</Label>
            <Input
              placeholder="Search by name…"
              value={accountSearch}
              onChange={(e) => setAccountSearch(e.target.value)}
              autoFocus
              data-testid="cqs-account-search"
            />
            <div className="border rounded divide-y max-h-[50vh] overflow-y-auto">
              {filteredAccounts.length === 0 && (
                <p className="text-sm text-slate-400 italic text-center py-6">No accounts match.</p>
              )}
              {filteredAccounts.map((a) => (
                <button
                  key={a.id}
                  onClick={() => setPicked(a)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50 flex justify-between items-center"
                  data-testid={`cqs-pick-${a.id}`}
                >
                  <div>
                    <div className="font-semibold text-sm text-slate-800">{a.name}</div>
                    <div className="text-xs text-slate-500">
                      {(a.contacts || []).length} contact{(a.contacts || []).length === 1 ? "" : "s"}
                      {a.last_campaign?.sent_at && <> · 📧 last campaign just sent</>}
                    </div>
                  </div>
                  <span className="text-xs text-blue-600">Choose →</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============== STAGE 2: template + recipients ==============
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="campaign-quick-send">
      <div className="bg-white rounded-lg w-full max-w-2xl my-10 shadow-2xl">
        <div className="border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Quick Send · {picked.name}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Logs a completed `email · outgoing` activity on this account automatically.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {account?.__pickAccount && (
              <button onClick={() => setPicked(null)} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1" data-testid="cqs-back">
                <ChevronLeft className="w-3.5 h-3.5" /> Change
              </button>
            )}
            <button onClick={onClose}><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Pre-selection hint banner */}
          {preselectedIds && preselectedIds.length > 0 && (
            <div className="rounded bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900 flex items-center gap-2" data-testid="cqs-preselected-banner">
              <Send className="w-3.5 h-3.5" />
              Pre-targeted to <strong>{preselectedIds.length}</strong> contact{preselectedIds.length === 1 ? "" : "s"} from the lead's Contacts tab.
              You can still tick / un-tick rows below.
            </div>
          )}

          {/* Template selector */}
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Template</Label>
            {loadingTemplates ? (
              <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
            ) : (
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm mt-1"
                data-testid="cqs-template"
              >
                {templates.length === 0 && <option value="">No templates available</option>}
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name || t.id}{t.target ? `  ·  ${t.target}` : ""}
                  </option>
                ))}
              </select>
            )}
            {templateId && (
              <button onClick={loadPreview} className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1" data-testid="cqs-preview">
                <ExternalLink className="w-3 h-3" /> Preview template
              </button>
            )}
          </div>

          {/* Subject override */}
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="cqs-subject"
            />
          </div>

          {/* Recipient mode */}
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold text-slate-500">Recipients</Label>
            <div className="mt-1 flex flex-wrap gap-2 text-sm">
              {[
                { v: "primary", l: `Primary contact only${primary ? ` · ${primary.name || primary.email}` : " (none yet)"}` },
                { v: "all", l: `All contacts on this account (${accountContacts.length})` },
                { v: "custom", l: "Custom selection…" },
              ].map((opt) => (
                <label key={opt.v} className={`px-3 py-1.5 rounded border cursor-pointer text-xs ${recipientMode === opt.v ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
                  <input
                    type="radio"
                    name="cqs-mode"
                    value={opt.v}
                    checked={recipientMode === opt.v}
                    onChange={() => setRecipientMode(opt.v)}
                    className="hidden"
                    data-testid={`cqs-mode-${opt.v}`}
                  />
                  {opt.l}
                </label>
              ))}
            </div>
            {recipientMode === "custom" && (
              <div className="mt-2 border rounded divide-y max-h-44 overflow-y-auto" data-testid="cqs-custom-list">
                {accountContacts.length === 0 && (
                  <p className="text-xs text-slate-400 italic text-center py-3">No contacts to choose from — add some first.</p>
                )}
                {accountContacts.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-slate-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!customSelected[c.id]}
                      onChange={(e) => setCustomSelected({ ...customSelected, [c.id]: e.target.checked })}
                      className="accent-emerald-600"
                    />
                    <span className="flex-1">
                      <span className="font-semibold">{c.name || c.email}</span>
                      {c.title && <span className="text-slate-500"> · {c.title}</span>}
                      <span className="text-blue-600 ml-2">{c.email}</span>
                    </span>
                    {c.is_primary && <span className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 rounded font-bold">PRIMARY</span>}
                  </label>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1">
              <Mail className="w-3 h-3" />
              Will send to <strong className="mx-1">{validRecipients.length}</strong> contact{validRecipients.length === 1 ? "" : "s"}.
            </p>
          </div>

          {showPreview && (
            <div className="border rounded p-2 bg-slate-50 max-h-72 overflow-y-auto" data-testid="cqs-preview-panel">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-bold text-slate-600">Preview</span>
                <button onClick={() => setShowPreview(false)} className="text-xs text-slate-500 hover:text-slate-800">Close</button>
              </div>
              <iframe
                title="preview"
                srcDoc={preview || ""}
                className="w-full h-56 border bg-white"
              />
            </div>
          )}
        </div>

        <div className="border-t px-6 py-3 flex justify-end gap-2 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
          <Button
            onClick={send}
            disabled={sending || validRecipients.length === 0 || !templateId}
            className="bg-blue-600 hover:bg-blue-700 gap-1.5"
            data-testid="cqs-send"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send to {validRecipients.length} contact{validRecipients.length === 1 ? "" : "s"}
          </Button>
        </div>
      </div>
    </div>
  );
}
